import React, { createContext, FC, Key, PropsWithChildren, useCallback, useContext, useMemo, useRef } from "react";
import { Dimensions, NativeMethods } from "react-native";
import invariant from "tiny-invariant";

type InViewOnChangeCallback = (inView: boolean) => void;

type MeasurableHTMLElement = Pick<NativeMethods, "measureInWindow">;

interface ElementsToCheckInView {
  readonly element: MeasurableHTMLElement;
  readonly onChange: InViewOnChangeCallback;
}

interface InViewProviderApi {
  readonly registerElement: (
    key: string,
    element: MeasurableHTMLElement | null,
    onChange: (inView: boolean) => void,
  ) => void;
  readonly checkIfRegisteredElementsAreInView: () => void;
}

const InViewContext = createContext<InViewProviderApi>(undefined as unknown as InViewProviderApi);

/**
 * We want to make 'key' required and not nullable just to force it to be set as way to flush the registered elements declaratively.
 * Think on categories grid loading products from different categories on each route.
 * Changing this key prop from one category to another (for example, assigning the categoryId as 'key') we'll force a rerender.
 * So the registered elements will be flushed instantly without any need of a explicit function call to do it.
 *
 * When the subtree must survive the change (e.g. category tabs that keep their scroll), pass 'resetKey' instead:
 * changing it flushes the registered elements without remounting the children.
 * */
interface InViewProviderProps {
  readonly key: Key;
  readonly resetKey?: Key;
}

const InViewProvider: FC<PropsWithChildren<InViewProviderProps>> = ({ children, resetKey }) => {
  const elementsRef = useRef<Record<string, ElementsToCheckInView>>({});
  const resetKeyRef = useRef(resetKey);

  if (resetKeyRef.current !== resetKey) {
    resetKeyRef.current = resetKey;
    elementsRef.current = {};
  }

  const measureElement = useCallback((element: MeasurableHTMLElement, onChange: (inView: boolean) => void) => {
    const offSetInPixels = 500;

    if (!element) {
      return;
    }

    element.measureInWindow(
      (_unusedXCoordinate: number, yCoordinate: number, _unusedElementWidth: number, elementHeight: number) => {
        const { height: windowHeight } = Dimensions.get("window");

        /**
         * The 'y' coordinate will point to the element's top corner. So if we dont take the element's height into consideration
         * and it's taller than the offSetInPixels, we can think that is out of the viewport when actually it's not.
         * It's top corner will be out because it's further than the offset but the bottom corner can still be on the viewport.
         *
         * Thus, to make sure that the WHOLE element is out of the viewport (plus an additional offset) we have to include it's
         * height in the equation.
         */
        const elementOffset = (elementHeight + offSetInPixels) * -1;

        const isInViewPort = yCoordinate > elementOffset && yCoordinate < windowHeight + offSetInPixels;

        onChange(isInViewPort);
      },
    );
  }, []);

  const checkIfRegisteredElementsAreInView = useCallback(() => {
    Object.values(elementsRef.current).forEach((elementTracker) => {
      measureElement(elementTracker.element, elementTracker.onChange);
    });
  }, [measureElement]);

  const registerElement = useCallback(
    (key: string, element: MeasurableHTMLElement | null, onChange: (inView: boolean) => void) => {
      if (!element || elementsRef.current[key] !== undefined) {
        return;
      }

      elementsRef.current[key] = { element, onChange };
      measureElement(element, onChange);
    },
    [measureElement],
  );

  const value = useMemo(
    () => ({
      registerElement,
      checkIfRegisteredElementsAreInView,
    }),
    [checkIfRegisteredElementsAreInView, registerElement],
  );

  return <InViewContext.Provider value={value}>{children}</InViewContext.Provider>;
};

const useRegisterElementForInView = () => {
  const { registerElement } = useContext(InViewContext);

  invariant(registerElement, "You are calling useRegisterElementForInView outside an InViewProvider");

  return registerElement;
};

const useCheckIfElementsAreInView = () => {
  const { checkIfRegisteredElementsAreInView } = useContext(InViewContext);

  invariant(checkIfRegisteredElementsAreInView, "You are calling useElementsInViewChecker outside an InViewProvider");

  return checkIfRegisteredElementsAreInView;
};

export { InViewProvider, useRegisterElementForInView, useCheckIfElementsAreInView };
